import Vue from 'vue';
import { getVariationGroupId } from '../../../common/helpers/productIdHelpers';

import { getOriginalPID } from '../../../common/utils/doubleLookUtils';
import getFrontConfig from '../../../common/getFrontConfig';

const FRONT_CONFIG = getFrontConfig();

export const types = {
  init: 'cart/init',
  loadRecommendations: 'cart/loadRecommendations',
  addItem: 'cart/addItem',
  removeItem: 'cart/removeItem',
  updateItem: 'cart/updateItem',
};

/**
 * @typedef VuexCartStateItem
 * @type {Object}
 * @property {string} id ID of the variant (color + size)
 * @property {string} variationGroupId ID of the parent variation group (color)
 * @property {Number} quantity number of added items
 * @property {Object} productLineItem Simple product line item
 * @property {Object} productLineItem.id ID of the variant added to cart
 * @property {Object} productLineItem.UUID unique ID of the line item
 */

/**
 * @typedef VuexCartState
 * @type {Object}
 * @property {Object.<string, VuexCartStateItem>} cart
 * @property {Object[]} recommendations Einstein recommended products for the
 * cart. Objects are product tiles with plain images
 * @property {string} removeProductLineItemUrl URL to removing endpoint (ex:
 * /on/demandware.store/Sites-byl-sg-Site/en_SG/Cart-RemoveProductLineItem)
 * @property {Object.<string, string[]>} variantIdsForVariationGroupId
 */

/** @type {VuexCartState} */
const initialState = {
  cart: {},
  recommendations: [],
  variantIdsForVariationGroupId: {},
};

const utils = {
  /**
   * Remove an entry from a given object
   * @param {Object} obj - target object
   * @param {string} key - target key
   * @returns {obj} - modified object
   */
  delete(obj, key) {
    Vue.delete(obj, key);
    return obj;
  },
  /**
   * Get an entry
   * @param {Object} obj - target object
   * @param {string} key - target key
   * @returns {Object} - Modified object
   */
  get(obj, key) {
    return obj[key];
  },
  /**
   * Check if an object contains a key
   * @param {Object} obj - target object
   * @param {string} key - target key
   * @returns {boolean} - true if the object contains the key
   */
  has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  },
  /**
   * Add item to the array of values or create new array for the key
   * @param {Object.<string, string[]>} obj
   * @param {string} key
   * @param {string} newArrayItem
   * @returns {Object.<string, string[]>}
   */
  pushValueToKey(obj, key, newArrayItem) {
    if (!this.has(obj, key)) {
      this.set(obj, key, []);
    }
    const items = this.get(obj, key);
    if (!items.includes(newArrayItem)) {
      // items.splice(items.length, 0, newArrayItem);
      items.push(newArrayItem);
    }
    return obj;
  },
  /**
   * Add or modify an entry
   * @param {Object} obj - target object
   * @param {string} key - target key
   * @param {any} value - value to set
   * @returns {Object} - Modified object
   */
  set(obj, key, value) {
    // eslint-disable-next-line no-param-reassign
    Vue.set(obj, key, value);
    return obj;
  },
};

/**
 * Parse product line item to vuex state item.
 * @param {ProductLineItem} productLineItem line item model
 * @returns {VuexCartStateItem} object for vuex state
 */
function createVuexCartStateItem(productLineItem) {
  const variantId = productLineItem.id;
  const variationGroupId = getVariationGroupId(variantId);
  return {
    id: variantId,
    variationGroupId,
    quantity: productLineItem.quantity,
    productLineItem: {
      id: variantId,
      UUID: productLineItem.UUID,
    },
  };
}

// actions to dispatch mutations
const actions = {
  /**
   * Initialize the module state from vuedata
   *
   * @param {Object} context Vuex context
   * @param {VuexCartState} context.state state to initialize
   * @param {Function} context.commit function to call mutations
   * @param {Function} context.dispatch function to call actions
   * @param {Cart} cart - SFRA Cart model from vuedata
   * @returns {void}
   */
  init({ state, commit }, cart) {
    const { items } = cart;
    const { removeProductLineItemUrl } = cart.actionUrls;
    state.removeProductLineItemUrl = removeProductLineItemUrl;
    items.forEach((item) => commit('addItem', item));
  },
  /**
   * Load the recommendations for the current basket
   * @param {Object} context Vuex context
   * @param {VuexCartState} context.state state
   * @param {Function} context.commit function to call mutations
   */
  loadRecommendations({ commit }) {
    return fetch(FRONT_CONFIG.urls.cartGetRecommendations)
      .then((response) => response.json())
      .then(({ recommendations = [] }) => {
        const recos = recommendations.filter(
          (item) => item.product?.id ?? false
        );
        commit('setRecommendations', recos);
        return recos;
      });
  },
};

export const getters = {
  includes: 'cart/includes',
  getProductLineItemsForVariationGroupId:
    'cart/getProductLineItemsForVariationGroupId',
  removeProductLineItemUrl: 'cart/removeProductLineItemUrl',
  totalQuantity: 'cart/totalQuantity',
};

// mutations to modify state
const mutations = {
  /**
   * Add or update a line item
   *
   * @param {VuexCartState} state state to initialize
   * @param {ProductLineItem} lineItem - SFRA Product line item
   * @returns {void}
   */
  addItem(state, lineItem) {
    const cartItem = createVuexCartStateItem(lineItem);
    utils.set(state.cart, lineItem.id, cartItem);
    utils.pushValueToKey(
      state.variantIdsForVariationGroupId,
      cartItem.variationGroupId,
      cartItem.id
    );
  },

  /**
   * Remove a line item
   *
   * @param {VuexCartState} state state to initialize
   * @param {string} id - Product line item ID (variant ID)
   * @returns {void}
   */
  removeItem(state, id) {
    utils.delete(state.cart, id);
    const variantGroupId = getVariationGroupId(id);
    const ids = utils.get(state.variantIdsForVariationGroupId, variantGroupId);
    if (ids.includes(id)) {
      if (ids.length > 1) {
        utils.set(
          state.variantIdsForVariationGroupId,
          variantGroupId,
          ids.filter((x) => x !== id)
        );
      } else {
        utils.delete(state.variantIdsForVariationGroupId, variantGroupId);
      }
    }
  },

  /**
   * Set the recommendations for the current cart
   *
   * @param {VuexCartState} state state to update
   * @param {Object[]} recommendations - Product Tiles of the recommendations of
   * Einstein
   * @returns {void}
   */
  setRecommendations(state, recommendations) {
    state.recommendations = recommendations;
  },

  /**
   * Update a line item
   *
   * @param {VuexCartState} state state to initialize
   * @param {ProductLineItem} lineItem - SFRA Product line item
   * @returns {void}
   */
  updateItem(state, lineItem) {
    const cartItem = createVuexCartStateItem(lineItem);
    utils.set(state.cart, lineItem.id, cartItem);
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  getters: {
    /**
     * Returns an arrow function to know if the cart contains any size of a product.
     * @param {VuexCartState} state - Cart state
     * @returns {Function(string):boolean} - Arrow function that returns a boolean
     */
    includes: (state) => (variationGroupId) => {
      // safeVariationGroupId has neither DL chars nor size code.
      const safeVariationGroupId = getVariationGroupId(
        getOriginalPID(variationGroupId)
      );
      return utils.has(
        state.variantIdsForVariationGroupId,
        safeVariationGroupId
      );
    },
    /**
     * Returns an arrow function to get every line refered to a given variation group ID.
     * @param {VuexCartState} state - Cart state
     * @returns {Function(string):ProductLineItem[]} - Related product line items
     */
    getProductLineItemsForVariationGroupId: (state) => (variationGroupId) => {
      const idWithoutDoubleLook = getOriginalPID(variationGroupId);
      const ids =
        state.variantIdsForVariationGroupId[idWithoutDoubleLook] ?? [];
      return ids.map((id) => state.cart[id].productLineItem);
    },
    /**
     * Returns the URL to the endpoint
     * @param {VuexCartState} state - Cart state
     * @returns {string} - URL to the endpoint
     */
    removeProductLineItemUrl: (state) => state.removeProductLineItemUrl,
    /**
     * Returns the total ammount of units added to cart.
     * @param {VuexCartState} state - Cart state
     * @returns {number} - Sum of all units
     */
    totalQuantity: (state) =>
      Object.values(state.cart).reduce(
        (res, lineItem) => res + lineItem.quantity,
        0
      ),
  },
  mutations,
};
